import type { PersonaCopyPools } from "./personaCopyKinds";

const NEUTRAL: PersonaCopyPools["neutral"] = {
	pollIntro: [
		`*{q}*\nRanked poll — vote privately, then the host closes when ready.`,
		`*{q}*\nYour ranking stays private until results publish. Tap *Vote* when ready.`,
		`*{q}*\nQuick ranked ballot in Slack — one ordering per person, host closes the poll.`,
		`*{q}*\nRank every option once (1 = top pick). The host wraps voting when you're done.`,
		`*{q}*\nOpen ranked poll — submit your order in the modal; we'll tally after close.`,
		`*{q}*\nHelp the group decide: rank options below, privately, before time runs out.`,
		`*{q}*\nRanked choice poll — no account needed. Vote in Slack; host publishes results.`,
		`*{q}*\nWe need your ordering on this one. Rank in the modal; host controls timing.`,
	],
	voteButton: ["Vote", "Rank options", "Open ballot", "Submit ranking", "Cast vote", "Add my order"],
	closeButton: ["Close poll", "End voting", "Close ballot", "Stop voting", "Finalize voting"],
	statusButton: ["Poll status", "Check status", "Host status", "Voting status", "Who voted"],
	remindHostButton: ["Remind channel", "Nudge channel", "Send reminder", "Ping channel", "Bump the poll"],
	remindHostSuccess: [
		"Reminder posted to the channel.",
		"Channel reminder is live.",
		"Nudge sent — the channel should see it now.",
		"Done — reminder posted.",
		"Reminder delivered to the channel.",
	],
	remindCalm: [
		`*Poll is open* — "{q}". Rank your choices in Slack when you have a minute.`,
		`*Still collecting* — "{q}". Add your ranking via *Vote* below.`,
		`*Your input helps* — "{q}" is open. Rank options in the modal when you can.`,
		`*Open ballot* — "{q}". One private ranking per person — tap *Vote*.`,
		`*We're gathering rankings* on "{q}". Jump in below when ready.`,
		`*Poll live* — "{q}". Rank every option; host closes when it's time.`,
		`*Quick favor* — rank "{q}" in Slack. Takes a minute; stays private.`,
		`*Ballot open* for "{q}". Submit your order before the host closes.`,
	],
	remindSoon: [
		`:hourglass: *Closing soon* — "{q}" needs your rankings. About {h}h left.`,
		`:hourglass: *Window narrowing* — "{q}" still needs ballots (~{h}h). Vote below.`,
		`:hourglass: *Time's running* on "{q}" — ~{h}h to rank. Use *Vote*.`,
		`:hourglass: *Heads up* — "{q}" closes in ~{h}h. Rankings still needed.`,
		`:hourglass: *Soon* — "{q}" wraps in ~{h}h. Submit your ordering.`,
		`:hourglass: *Don't sit this out* — "{q}" (~{h}h left). Rank in Slack.`,
		`:hourglass: *Almost closed* — "{q}". ~{h}h remaining for your vote.`,
		`:hourglass: *Last stretch* for "{q}" — ~{h}h. Rank via the button.`,
	],
	remindLastCall: [
		`:rotating_light: *Last call* — "{q}" closes in under {h}h. Please vote now.`,
		`:rotating_light: *Final window* — "{q}" (<{h}h). Rank now or miss the tally.`,
		`:rotating_light: *Urgent* — "{q}" is about to close. Submit rankings now.`,
		`:rotating_light: *Closing imminently* — "{q}". Under {h}h — vote immediately.`,
		`:rotating_light: *Now or never* (almost) — "{q}" < {h}h. Tap *Vote*.`,
		`:rotating_light: *Last chance* on "{q}" — under {h}h. Rank every option.`,
		`:rotating_light: *Final nudge* — "{q}" shuts soon (<{h}h). Vote in Slack.`,
		`:rotating_light: *Wrap-up soon* — "{q}". <{h}h left — rankings required.`,
	],
	voteModalTitle: ["Your ballot", "Rank options", "Your ranking", "Private ballot", "Cast vote"],
	voteModalIntro: [
		`*{q}*\nAssign a unique rank to each option (1 = best).`,
		`*{q}*\nStack-rank every choice — 1 is your top pick. Others stay private.`,
		`*{q}*\nGive each option a distinct rank (1 = strongest preference).`,
		`*{q}*\nOrder the list once: 1 = first choice. No duplicate ranks.`,
		`*{q}*\nRank all options; your ballot won't be shown to others.`,
		`*{q}*\nOne rank per option (1 = best). Submit when your ordering is set.`,
	],
	voteSubmit: ["Submit vote", "Submit ranking", "Lock ballot", "Send ranking", "Confirm order"],
	createModalTitle: ["New poll", "Start poll", "Create poll", "New ranked poll", "Open ballot"],
	createSubmit: ["Create", "Start poll", "Launch poll", "Open poll", "Go live"],
	gatherThreadIntro: [
		`*{q}*\nReply in this thread with one option per message. The host closes nominations when ready.`,
		`*{q}*\nCrowdsource answers here — one proposal per reply. The host launches the ranked poll after close.`,
		`*{q}*\nNominate options below — one per reply. Host closes intake, then opens the ranked ballot.`,
		`*{q}*\nThread proposals become poll choices after the host closes gathering.`,
		`*{q}*\nAdd ideas in replies; duplicates merge when the host builds the ballot.`,
		`*{q}*\nOpen brainstorm — reply with a single option each. Host finishes with *Close & build ballot*.`,
		`*{q}*\nCollect options here first; ranked voting starts after the host closes nominations.`,
	],
};

const ANALYST: PersonaCopyPools["analyst"] = {
	pollIntro: [
		`*{q}*\nRanked input requested — submit a full ordering; host finalizes the dataset.`,
		`*{q}*\nCollecting complete ranking vectors — modal below; host closes the window.`,
		`*{q}*\nSample open: rank all options (1 = max utility). Results aggregate after close.`,
		`*{q}*\nStructured preference capture — one permutation per respondent via *Vote*.`,
		`*{q}*\nRanked survey live — submit ordering; Borda tally on publish.`,
		`*{q}*\nData collection: rank each option exactly once. Host ends collection when ready.`,
		`*{q}*\nInput window open — provide a total order over options; private ballots.`,
		`*{q}*\nPreference elicitation — rank 1…n; host triggers finalize + distribution recap.`,
	],
	voteButton: [
		"Submit rankings",
		"Enter vector",
		"Record order",
		"Add sample",
		"Rank all",
		"Input ballot",
	],
	closeButton: ["Close poll", "End collection", "Close window", "Finalize sample", "Stop intake"],
	statusButton: ["Poll status", "Sample status", "Collection status", "Field status", "Response status"],
	remindHostButton: ["Nudge channel", "Ping respondents", "Boost response rate", "Send nudge", "Remind sample"],
	remindHostSuccess: [
		"Channel nudge sent.",
		"Reminder dispatched to the channel.",
		"Nudge live — expect another response tranche.",
		"Done — channel reminded to complete rankings.",
		"Outbound nudge posted.",
	],
	remindCalm: [
		`:bar_chart: *Sample still open* — "{q}". Add your ranking vector via the button below.`,
		`:bar_chart: *Collection ongoing* — "{q}". Submit a full permutation when ready.`,
		`:bar_chart: *Missing ballots* on "{q}". Rank all options in the modal.`,
		`:bar_chart: *Open window* — "{q}". One complete ordering per participant.`,
		`:bar_chart: *Field note* — "{q}" still accepting ranked inputs.`,
		`:bar_chart: *Incomplete coverage* for "{q}". Tap below to add your vector.`,
		`:bar_chart: *Active collection* — "{q}". Rank 1…n; ballots stay private.`,
		`:bar_chart: *Data gap* — "{q}" needs your ranking before close.`,
	],
	remindSoon: [
		`:bar_chart: *Window closing* — "{q}" still needs ballots (~{h}h). Rank all options below.`,
		`:bar_chart: *T−{h}h* — "{q}" approaches cutoff. Complete your ordering.`,
		`:bar_chart: *Response rate risk* — "{q}" (~{h}h). Submit ranking vector.`,
		`:bar_chart: *Cutoff approaching* on "{q}" — ~{h}h. Fill the ballot.`,
		`:bar_chart: *Low time remaining* — "{q}" (~{h}h). Rank every option.`,
		`:bar_chart: *Imminent close* — "{q}" in ~{h}h. Lock in permutation.`,
		`:bar_chart: *Final tranche* — "{q}" (~{h}h left). Vote below.`,
		`:bar_chart: *Collection taper* — "{q}" ~{h}h. Complete ranked input.`,
	],
	remindLastCall: [
		`:bar_chart: *Final collection* — "{q}" closes in <{h}h. Complete your ranking now.`,
		`:bar_chart: *Hard stop soon* — "{q}" (<{h}h). Submit full ordering immediately.`,
		`:bar_chart: *Terminal window* — "{q}". <{h}h — last chance for ballot.`,
		`:bar_chart: *Closure imminent* on "{q}" (<{h}h). Rank now.`,
		`:bar_chart: *End of sample* — "{q}" < {h}h. Final rankings required.`,
		`:bar_chart: *Last intake* — "{q}" shutting down (<{h}h).`,
		`:bar_chart: *Critical path* — "{q}" <{h}h. Complete vector now.`,
		`:bar_chart: *Zero slack* — "{q}" under {h}h. Submit permutation.`,
	],
	voteModalTitle: ["Ranking form", "Preference form", "Ballot matrix", "Order input", "Rank capture"],
	voteModalIntro: [
		`*{q}*\nRank each option exactly once (1 = highest preference).`,
		`*{q}*\nAssign ranks 1…n with no ties — 1 = top of your utility order.`,
		`*{q}*\nProvide a strict total order. Ballots are confidential.`,
		`*{q}*\nOne rank per row; 1 = maximum preference signal.`,
		`*{q}*\nComplete the permutation — duplicates invalidate the sample point.`,
		`*{q}*\nStack-rank options; aggregated Borda follows host publish.`,
	],
	voteSubmit: ["Lock in ranking", "Commit order", "Submit vector", "Finalize ballot", "Record ranking"],
	createModalTitle: ["New poll", "New collection", "Open sample", "Start survey", "New intake"],
	createSubmit: ["Create", "Open window", "Start collection", "Launch sample", "Begin intake"],
	gatherThreadIntro: [
		`*{q}*\nCollecting candidate options in-thread — one label per reply. Host closes intake before the ranked poll.`,
		`*{q}*\nOpen nomination thread: one option per message. Host finalizes the option set, then opens the ballot.`,
		`*{q}*\nInput window: one proposal per reply. Host ends collection, then ranked poll.`,
		`*{q}*\nStructured option intake in-thread — host closes before ballot launch.`,
		`*{q}*\nNominate labels below; dedupe on close. Host opens ranked survey next.`,
		`*{q}*\nField collection live — single option per message until host closes.`,
		`*{q}*\nCandidate set built from replies; host triggers ballot after close.`,
	],
};

const EXECUTIVE: PersonaCopyPools["executive"] = {
	pollIntro: [
		`*{q}*\nPlease rank options privately. The host will close and publish when ready.`,
		`*{q}*\nInput requested — rank choices below; results follow host close.`,
		`*{q}*\nDecision support poll: submit your preference order confidentially.`,
		`*{q}*\nRanked feedback needed on this item. Vote via modal; host controls timeline.`,
		`*{q}*\nPlease provide a clear ordering (1 = top choice). Host publishes summary.`,
		`*{q}*\nTeam poll — rank options once. Ballots remain private until publish.`,
		`*{q}*\nAction: rank all alternatives. Host will close voting and share results.`,
		`*{q}*\nStructured input requested — one ranking per participant before deadline.`,
	],
	voteButton: ["Cast vote", "Submit input", "Record preference", "Rank choices", "Add feedback", "Vote now"],
	closeButton: ["Close poll", "Close voting", "End poll", "Conclude voting", "Stop collection"],
	statusButton: ["Poll status", "Status", "Voting status", "Progress", "Turnout"],
	remindHostButton: ["Remind team", "Nudge team", "Send reminder", "Prompt team", "Follow up"],
	remindHostSuccess: [
		"Team reminder posted.",
		"Reminder sent to the channel.",
		"Follow-up delivered — team notified.",
		"Done — team nudge is live.",
		"Channel reminder complete.",
	],
	remindCalm: [
		`*Action requested* — feedback needed on "{q}". Use *Vote* below.`,
		`*Input still open* — "{q}". Please rank options at your earliest convenience.`,
		`*Decision poll live* — "{q}". Submit your ordering via the button.`,
		`*Feedback requested* on "{q}". Rank privately in Slack.`,
		`*Open item* — "{q}". Your ranking helps close the loop.`,
		`*Please weigh in* — "{q}" is collecting ranked responses.`,
		`*Ballot open* for "{q}". One ordering per colleague.`,
		`*Rank when ready* — "{q}" remains open for input.`,
	],
	remindSoon: [
		`*Deadline approaching* — "{q}" closes in ~{h}h. Rankings still required.`,
		`*Time-sensitive* — "{q}" (~{h}h). Submit your preference order.`,
		`*Approaching close* on "{q}" — ~{h}h remaining. Vote below.`,
		`*Window closing* — "{q}" needs input within ~{h}h.`,
		`*Prioritize* — "{q}" (~{h}h). Rank all options now.`,
		`*Soon* — "{q}" wraps in ~{h}h. Complete ballot.`,
		`*Reminder* — "{q}" ~{h}h left. Rankings outstanding.`,
		`*Closing ~{h}h* — "{q}". Submit ranking via *Vote*.`,
	],
	remindLastCall: [
		`*Final reminder* — "{q}" closes in under {h}h. Submit rankings now.`,
		`*Last window* — "{q}" (<{h}h). Rank immediately.`,
		`*Urgent* — "{q}" closing imminently. Input required now.`,
		`*Final call* on "{q}" — under {h}h. Complete ballot.`,
		`*Close imminent* — "{q}" < {h}h. Vote before publish.`,
		`*Immediate action* — "{q}" shuts in <{h}h.`,
		`*Last chance* — "{q}" (<{h}h). Rank every option.`,
		`*Wrap-up* — "{q}" under {h}h. Submit now.`,
	],
	voteModalTitle: ["Your ballot", "Preference ballot", "Input form", "Your ranking", "Feedback form"],
	voteModalIntro: [
		`*{q}*\nRank each option (1 = top preference).`,
		`*{q}*\nOrder choices 1…n — 1 = strongest preference. Confidential.`,
		`*{q}*\nProvide a single ranked list; 1 = first choice.`,
		`*{q}*\nRank all options once. Results aggregate after host close.`,
		`*{q}*\nAssign unique ranks — top pick = 1.`,
		`*{q}*\nStructured input: total order over options.`,
	],
	voteSubmit: ["Submit", "Submit ranking", "Send input", "Confirm", "Record vote"],
	createModalTitle: ["New poll", "New decision poll", "Start poll", "Open feedback", "New ballot"],
	createSubmit: ["Create", "Launch", "Open poll", "Start", "Open voting"],
	gatherThreadIntro: [
		`*{q}*\nWe're collecting options in this thread before the ranked vote. One proposal per reply.`,
		`*{q}*\nNominate choices here — the host will close intake and open the decision poll.`,
		`*{q}*\nOptions intake in-thread — one per reply. Leadership closes, then ranked ballot.`,
		`*{q}*\nProposals welcome below; host consolidates before the vote.`,
		`*{q}*\nBrief thread brainstorm — single option each. Host opens poll after close.`,
		`*{q}*\nGather candidate answers here; ranked decision follows host close.`,
		`*{q}*\nOne line per option in replies; host finalizes the slate for voting.`,
	],
};

const PLAY_BY_PLAY: PersonaCopyPools["play_by_play"] = {
	pollIntro: [
		`*{q}*\n:studio_microphone: Ranked ballot — get your picks in before the host blows the whistle!`,
		`*{q}*\n:studio_microphone: The poll's live — stack-rank your lineup in the modal!`,
		`*{q}*\n:studio_microphone: Fans at home, it's your turn: rank every option once!`,
		`*{q}*\n:studio_microphone: We're taking ranked picks on this one — tap *Vote* to play!`,
		`*{q}*\n:studio_microphone: Ballot's open — get your order in before the clock runs out!`,
		`*{q}*\n:studio_microphone: Rank 'em 1 through n — host calls the game when voting ends!`,
		`*{q}*\n:studio_microphone: Your ballot counts — private ranking, public results after close!`,
		`*{q}*\n:studio_microphone: Step up: rank the field before the final horn!`,
	],
	voteButton: [
		"Get in the game",
		"Submit ballot",
		"Rank my picks",
		"Play ball",
		"Cast lineup",
		"Take the floor",
	],
	closeButton: ["End the clock", "Blow the whistle", "Final horn", "Game over", "Stop the clock"],
	statusButton: ["Scoreboard", "Box score", "Game status", "The board", "Stat sheet"],
	remindHostButton: ["Hype the crowd", "Rally the room", "Pump up channel", "Get loud", "Fire up voters"],
	remindHostSuccess: [
		"Crowd hyped — reminder's in the channel!",
		"Reminder's on the board — channel should feel it!",
		"Buzzer beater nudge sent!",
		"That's in the arena — reminder posted!",
		"Play's been signaled — check the channel!",
	],
	remindCalm: [
		`:studio_microphone: *We're live!* "{q}" needs your rankings — step up and vote!`,
		`:studio_microphone: *Still in play* — "{q}". Get your ballot on the board!`,
		`:studio_microphone: *The poll's rolling* on "{q}" — rank your picks below!`,
		`:studio_microphone: *Crowd participation* wanted for "{q}" — tap *Vote*!`,
		`:studio_microphone: *On the clock* (plenty of time) for "{q}" — submit your order!`,
		`:studio_microphone: *Your turn in the rotation* — rank "{q}"!`,
		`:studio_microphone: *Ballot open* — "{q}" won't tally itself. Rank in Slack!`,
		`:studio_microphone: *Warm-up's over* — real picks needed on "{q}"!`,
	],
	remindSoon: [
		`:studio_microphone: *Two-minute warning!* "{q}" closes in ~{h}h — get your ballot in!`,
		`:studio_microphone: *Clock winding down* on "{q}" (~{h}h) — rank now!`,
		`:studio_microphone: *Crunch time approaching* — "{q}" ~{h}h left!`,
		`:studio_microphone: *Pressure building* — "{q}" in ~{h}h. Vote!`,
		`:studio_microphone: *Fourth quarter energy* — "{q}" (~{h}h). Submit lineup!`,
		`:studio_microphone: *Don't wait for OT* — "{q}" ~{h}h. Rank below!`,
		`:studio_microphone: *The horn's coming* — "{q}" ~{h}h. Ballot time!`,
		`:studio_microphone: *Sprint to the wire* — "{q}" (~{h}h). Get in!`,
	],
	remindLastCall: [
		`:studio_microphone: *FINAL WHISTLE soon!* "{q}" — under {h}h left. Vote NOW!`,
		`:studio_microphone: *BUZZER BEATER* — "{q}" < {h}h. Rank or ride the bench!`,
		`:studio_microphone: *NO TIMEOUTS LEFT* on "{q}" — <{h}h. Ballot NOW!`,
		`:studio_microphone: *GAME ON THE LINE* — "{q}" under {h}h. Vote!`,
		`:studio_microphone: *LAST POSSESSION* — "{q}" <{h}h. Submit ranking!`,
		`:studio_microphone: *ALL OR NOTHING* — "{q}" shuts in <{h}h!`,
		`:studio_microphone: *FINAL SECONDS* — "{q}". Vote before the horn!`,
		`:studio_microphone: *OT DENIED* — close now on "{q}" (<{h}h)!`,
	],
	voteModalTitle: ["Your lineup", "Ballot card", "Pick sheet", "Your card", "Rank card"],
	voteModalIntro: [
		`*{q}*\nStack-rank every option (1 = your #1 pick).`,
		`*{q}*\nBuild your lineup — 1 is the starter, no duplicate slots.`,
		`*{q}*\nRank the field top to bottom; scouts won't see individual ballots.`,
		`*{q}*\nOne rank per player on the board — 1 = MVP pick.`,
		`*{q}*\nSet your order before the horn — 1 = first team.`,
		`*{q}*\nFull ranking required — that's how we call the winner.`,
	],
	voteSubmit: ["Submit ballot", "Lock picks", "Send lineup", "Confirm card", "Play it"],
	createModalTitle: ["New poll", "New matchup", "Drop the puck", "Open ballot", "New game"],
	createSubmit: ["Drop the puck", "Start the clock", "Face-off", "Kick off", "Play ball"],
	gatherThreadIntro: [
		`:studio_microphone: *{q}*\nDrop your picks in this thread — one option per reply. Host calls time when the roster's set.`,
		`:studio_microphone: *{q}*\nScout options here; host blows the whistle and opens the ranked ballot.`,
		`:studio_microphone: *{q}*\nCall your shot in replies — one nominee per message till the host ends the clock.`,
		`:studio_microphone: *{q}*\nFans propose plays below; host sets the lineup, then ranked voting.`,
		`:studio_microphone: *{q}*\nThread's open for nominations — one option each before face-off.`,
		`:studio_microphone: *{q}*\nStack the board in replies; host closes scouting and drops the puck on the poll.`,
		`:studio_microphone: *{q}*\nNominate in-thread; whistle blows when the host builds the ballot.`,
	],
};

const PIRATE: PersonaCopyPools["pirate"] = {
	pollIntro: [
		`:skull_crossbones: *{q}*\nRank yer choices in the ballot below. The cap'n closes when all hands have spoken.`,
		`:skull_crossbones: *{q}*\nEvery hand ranks the treasure list — vote in the modal, maties.`,
		`:skull_crossbones: *{q}*\nShip's ballot is open — stack yer preferences afore we tally.`,
		`:skull_crossbones: *{q}*\nThe crew decides: rank each option once (1 = finest loot).`,
		`:skull_crossbones: *{q}*\nYer counsel stays private — rank below, cap'n publishes the map later.`,
		`:skull_crossbones: *{q}*\nAll hands on deck — cast yer ordering ere the tide turns.`,
		`:skull_crossbones: *{q}*\nParley via ranked ballot — 1 = top treasure, no peekin' at other hands.`,
		`:skull_crossbones: *{q}*\nThe fleet needs yer ranking on this voyage. Vote in Slack!`,
	],
	voteButton: [
		"Cast yer vote",
		"Stow yer pick",
		"Mark the chart",
		"Sign the log",
		"Hoist ballot",
		"Rank the loot",
	],
	closeButton: ["Weigh anchor", "Dock the poll", "Lower the flag", "End the voyage", "Close the hold"],
	statusButton: ["Ship's log", "Crew roll", "Deck report", "Manifest", "Watch status"],
	remindHostButton: ["Rally the crew", "Beat the drum", "Summon hands", "Pipe all hands", "Wake the watch"],
	remindHostSuccess: [
		"Aye — the crew's been rallied. Reminder posted!",
		"Drum's been beat — reminder's in the channel!",
		"Hands summoned — check the main deck (channel).",
		"Aye aye — reminder sailed to the crew.",
		"The parrot's squawked — reminder live!",
	],
	remindCalm: [
		`:skull_crossbones: *Ahoy, crew!* Still time to rank *{q}* — every hand on deck, cast yer vote below!`,
		`:skull_crossbones: *Fair winds* on *{q}* — stow yer rankings in the ballot chest.`,
		`:skull_crossbones: *The hold's open* for *{q}*. Rank the loot list, matey.`,
		`:skull_crossbones: *Crew still wanted* — *{q}* won't tally itself. Vote below!`,
		`:skull_crossbones: *Steady as she goes* — rank *{q}* afore the cap'n closes.`,
		`:skull_crossbones: *Yer voice counts* on *{q}*. Private ballot — tap to rank.`,
		`:skull_crossbones: *All hands* — *{q}* needs yer ordering on the chart.`,
		`:skull_crossbones: *Treasure map incomplete* without ye on *{q}*. Rank in Slack!`,
	],
	remindSoon: [
		`:skull_crossbones: *Tide's turnin'!* *{q}* closes in ~{h}h — hoist yer rankings afore we tally!`,
		`:skull_crossbones: *Storm on the horizon* — *{q}* ~{h}h. Rank now, landlubbers!`,
		`:skull_crossbones: *Shoals ahead* for *{q}* (~{h}h). Stow yer ballot!`,
		`:skull_crossbones: *Sun's low* — *{q}* wraps in ~{h}h. Chart yer picks!`,
		`:skull_crossbones: *Nearly port* on *{q}* — ~{h}h. Every hand vote!`,
		`:skull_crossbones: *Ropes tighten* — *{q}* ~{h}h left. Rank the list!`,
		`:skull_crossbones: *Kraken time* approaches for *{q}* (~{h}h). Vote!`,
		`:skull_crossbones: *No dawdlin'* — *{q}* ~{h}h. Cast below!`,
	],
	remindLastCall: [
		`:skull_crossbones: *LAST CALL, maties!* *{q}* — under {h}h! Vote now or walk the plank!`,
		`:skull_crossbones: *Davy Jones knocks* — *{q}* < {h}h. Rank NOW!`,
		`:skull_crossbones: *Mutiny of silence* ends now — *{q}* <{h}h. Ballot!`,
		`:skull_crossbones: *Final bell* on *{q}* (<{h}h). Hoist yer vote!`,
		`:skull_crossbones: *No quarter* — *{q}* shuts in <{h}h. Every hand!`,
		`:skull_crossbones: *Plank's out* for slackers on *{q}* — <{h}h. Vote!`,
		`:skull_crossbones: *Black flag time* — *{q}* under {h}h. Rank or regret!`,
		`:skull_crossbones: *Cap'n's patience spent* — *{q}* <{h}h. Stow ballot now!`,
	],
	voteModalTitle: ["Yer ballot", "Treasure list", "Yer chart", "Crew ballot", "The log"],
	voteModalIntro: [
		`*{q}*\nRank each option in order (1 = finest treasure). Keep yer counsel — no peekin' at other hands.`,
		`*{q}*\nStack the loot 1…n — top pick = 1. Yer hand stays hidden.`,
		`*{q}*\nChart yer preferences — one rank per chest, no duplicates.`,
		`*{q}*\nOrder the spoils; the crew won't see individual maps.`,
		`*{q}*\nRank all plunder once — cap'n tallies after close.`,
		`*{q}*\nYer private ranking — 1 = crown jewel.`,
	],
	voteSubmit: ["Stow ballot", "Lock the vote", "Send to hold", "Commit chart", "Aye, rank it"],
	createModalTitle: ["New voyage", "New expedition", "Set sail", "Chart a poll", "New chart"],
	createSubmit: ["Launch poll", "Cast off", "Hoist sail", "Embark", "Set course"],
	gatherThreadIntro: [
		`:skull_crossbones: *{q}*\nPropose loot in this thread — one treasure per reply. Cap'n closes the haul afore the ballot.`,
		`:skull_crossbones: *{q}*\nCrew nominates options here; cap'n stows the list and opens the ranked vote.`,
		`:skull_crossbones: *{q}*\nOne chest per reply, maties — cap'n tallies when gathering ends.`,
		`:skull_crossbones: *{q}*\nChart yer nominees below; duplicates walk the plank on close.`,
		`:skull_crossbones: *{q}*\nAll hands propose a single option each — cap'n seals the manifest, then ballot.`,
		`:skull_crossbones: *{q}*\nThread be for nominations; ranked plunder comes after cap'n says done.`,
		`:skull_crossbones: *{q}*\nSpeak yer option in replies; cap'n hoists the final slate for voting.`,
	],
};

const WITTY: PersonaCopyPools["witty"] = {
	pollIntro: [
		`*{q}*\n:zap: Ranked poll — vote in the modal, try not to overthink it (we still will).`,
		`*{q}*\n:zap: Hot take season — rank options; drama arrives after close.`,
		`*{q}*\n:zap: One ranking per human — no receipts until publish.`,
		`*{q}*\n:zap: Your opinion, ordered — tap *Vote* before overthinking wins.`,
		`*{q}*\n:zap: Rank 'em like a group chat poll, but with actual math.`,
		`*{q}*\n:zap: Ballot's open — rank everything once (1 = you'd actually pick it).`,
		`*{q}*\n:zap: Decision fatigue friendly: one modal, one ordering, done.`,
		`*{q}*\n:zap: Help the room pick — privately rank below; host closes when ready.`,
	],
	voteButton: [
		"Spill the tea",
		"Drop rankings",
		"Send takes",
		"Vote (no cap)",
		"Rank it out",
		"Your move",
	],
	closeButton: ["Close poll", "Kill the bit", "Wrap it", "That's enough", "End the thread"],
	statusButton: ["Poll status", "Tea status", "Vibe check", "Who showed up", "Headcount"],
	remindHostButton: ["Ping the room", "Stir the pot", "Bump the thread", "Nudge the chat", "Rattle cages"],
	remindHostSuccess: [
		"Room pinged — reminder's live.",
		"Pot stirred — channel should notice.",
		"Nudge delivered. May the odds improve.",
		"Done — reminder's out there doing its thing.",
		"Ping sent. Back to pretending we're calm.",
	],
	remindCalm: [
		`:zap: *Still open* — "{q}" wants your hot takes… I mean, rankings. Tap *Vote*.`,
		`:zap: *Low-key reminder* — "{q}" won't rank itself. Your turn.`,
		`:zap: *The poll that keeps giving* — "{q}". Add your ordering.`,
		`:zap: *Friendly nudge* on "{q}" — modal below, no judgment (much).`,
		`:zap: *We're still collecting* on "{q}". Rank when the muse strikes.`,
		`:zap: *Your ranking is the missing ingredient* for "{q}".`,
		`:zap: *Open mic for preferences* — "{q}". Vote in Slack.`,
		`:zap: *Don't ghost the poll* — "{q}" needs your stack-rank.`,
	],
	remindSoon: [
		`:zap: *Clock's ticking* — "{q}" needs you (~{h}h). Rank 'em before FOMO kicks in.`,
		`:zap: *Almost crickets* on "{q}" — ~{h}h left. Vote!`,
		`:zap: *Main character moment* — "{q}" closes ~{h}h. Rank now.`,
		`:zap: *Snooze button wearing out* — "{q}" (~{h}h). Ballot time.`,
		`:zap: *Plot twist: deadline* — "{q}" in ~{h}h. Submit order.`,
		`:zap: *The group chat is waiting* — "{q}" ~{h}h. Rank below.`,
		`:zap: *Panic-adjacent timing* — "{q}" ~{h}h. You know what to do.`,
		`:zap: *Soon™* — "{q}" ~{h}h. Rank every option.`,
	],
	remindLastCall: [
		`:zap: *Seriously now* — "{q}" closes in <{h}h. Vote or forever hold your peace.`,
		`:zap: *This is the part in the movie* — "{q}" <{h}h. Rank NOW.`,
		`:zap: *No more "I'll do it later"* — "{q}" under {h}h.`,
		`:zap: *Final boss: procrastination* — "{q}" <{h}h. Vote!`,
		`:zap: *The door's closing* on "{q}" (<{h}h). Last rankings!`,
		`:zap: *Emoji-worthy urgency* — "{q}" <{h}h. Tap *Vote*.`,
		`:zap: *Ok actually last call* — "{q}" under {h}h.`,
		`:zap: *We're not mad, just closing* — "{q}" <{h}h. Rank!`,
	],
	voteModalTitle: ["Your ballot", "Ranking booth", "Take zone", "Order form", "Hot take desk"],
	voteModalIntro: [
		`*{q}*\nRank each option once (1 = you'd actually pick it).`,
		`*{q}*\nStack-rank — 1 = least regrettable choice.`,
		`*{q}*\nOne rank per row; duplicates are a social faux pas here.`,
		`*{q}*\nOrder the list; your ballot stays off the group chat.`,
		`*{q}*\nRank honestly — we aggregate, we don't judge (much).`,
		`*{q}*\nFull ordering required — 1 = top shelf pick.`,
	],
	voteSubmit: ["Send it", "Ship ranking", "Commit take", "Lock it in", "Yeet ballot"],
	createModalTitle: ["New poll", "New chaos", "Fresh poll", "Start the bit", "New thread"],
	createSubmit: ["Ship it", "Let's go", "Launch", "Send poll", "Unleash"],
	gatherThreadIntro: [
		`*{q}*\n:zap: Pitch options in-thread — one idea per reply. Host wraps nominations, then we rank.`,
		`*{q}*\n:zap: Crowdsource the menu here; host closes the brainstorm and ships the ranked poll.`,
		`*{q}*\n:zap: One hot take per reply — host curates the list, then we rank for real.`,
		`*{q}*\n:zap: Brainstorm below; duplicate vibes merge when the host closes.`,
		`*{q}*\n:zap: Drop options in-thread; ranked chaos follows host sign-off.`,
		`*{q}*\n:zap: Nominate in replies — host locks the slate, then ballot goes live.`,
		`*{q}*\n:zap: Thread = suggestion box. Host empties it into a ranked poll.`,
	],
};

export const PERSONA_COPY_POOLS: PersonaCopyPools = {
	neutral: NEUTRAL,
	analyst: ANALYST,
	executive: EXECUTIVE,
	play_by_play: PLAY_BY_PLAY,
	pirate: PIRATE,
	witty: WITTY,
};
